import * as d3 from 'd3';
import { Duration } from 'luxon';

import { bytesPerSecond, formatBytes, formatDataRateBits, kilobitsPerSecond } from './data_rates';

export const defaultIntFormatter = new Intl.NumberFormat('en-US');

// Data rate wrappers (simple API)
export const dataRateValueFormatter = (value: number, precision = 2) =>
  formatDataRateBits(value, kilobitsPerSecond, precision);

export const dataRateValueFormatterShort = (value: number) =>
  formatDataRateBits(value, kilobitsPerSecond, 0, 's');

export const dataRateBytesValueFormatter = (value: number) =>
  formatDataRateBits(value, bytesPerSecond);

// Usage/bytes wrappers
export const usageValueFormatter = (value: number, precision = 2) => formatBytes(value, precision);

export const usageValueFormatterShort = (value: number) => formatBytes(value, 0);

// Percentages
export const percentValueFormatter = d3.format('.2~%');

// Signal/RF
export const dbmValueFormatter = (value: number) => `${value.toFixed(0)} dBm`;
export const snrValueFormatter = (value: number) => `${value.toFixed(2)} dB`;
export const snrValueFormatterShort = (value: number) => `${value.toFixed(0)} dB`;

// Counts/packets
export const nonZeroCountFormatter = (value: number) => {
  if (value < 0) return '';
  return value.toFixed(0);
};
export const packetValueFormatterWithoutUnit = (value: number) => d3.format(',.0~f')(value);
export const packetValueFormatter = (value: number) => `${d3.format(',.0~f')(value)} packets`;
export const packetRateValueFormatter = (value: number) => {
  const absValue = Math.abs(value);

  if (absValue === 0) return '0 pps';
  if (absValue < 0.0001) return `${d3.format(',.6~f')(value)} pps`;
  if (absValue < 0.01) return `${d3.format(',.4~f')(value)} pps`;
  if (absValue < 1) return `${d3.format(',.3~f')(value)} pps`;
  return `${d3.format(',.2~f')(value)} pps`;
};
export const packetsPerSecondValueFormatter = (value: number) => `${value.toFixed(0)} pps`;
export const requestRateValueFormatter = (value: number) => {
  const absValue = Math.abs(value);

  if (absValue === 0) return '0 rps';
  if (absValue < 0.0001) return `${d3.format(',.6~f')(value)} rps`;
  if (absValue < 0.01) return `${d3.format(',.4~f')(value)} rps`;
  if (absValue < 1) return `${d3.format(',.3~f')(value)} rps`;
  return `${d3.format(',.2~f')(value)} rps`;
};

// Temperature
export const celsiusToFahrenheit = (value: number) => (value * 9) / 5 + 32;
export const fahrenheitToCelsius = (value: number) => ((value - 32) * 5) / 9;
export const temperatureFahrenheitFormatter = (value: number) => `${d3.format(',.2~f')(value)}°F`;
export const temperatureCelsiusFormatter = (value: number) => `${d3.format(',.2~f')(value)}°C`;
export const temperatureFahrenheitAndCelsiusFormatter = (value: number) =>
  `${d3.format(',.2~f')(value)}°F (${d3.format(',.2~f')(fahrenheitToCelsius(value))}°C)`;

// Power/electrical
export const powerFormatter = (value: number) =>
  value > 999 ? `${d3.format(',.2~f')(value / 1000)} kW` : `${d3.format(',.2~f')(value)} W`;
export const voltageFormatter = (value: number) => `${d3.format(',.2~f')(value)} V`;
export const currentFormatter = (value: number) => `${d3.format(',.2~f')(value)} A`;

// Frequency/RPM
export const frequencyFormatter = (value: number) => `${d3.format(',.2~f')(value)} Hz`;
export const rpmValueFormatter = (value: number) => `${d3.format(',.2~f')(value)} RPM`;
export const rpmValueFormatterShort = (value: number) => `${d3.format('~s')(value)} RPM`;

// Time
export const milliSecondFormatter = (value: number) =>
  value >= 1000 ? `${value / 1000}s` : `${d3.format(',.2~f')(value)}ms`;

export const durationValueFormatter = (seconds: number) => {
  const d = Duration.fromMillis(seconds * 1000);
  return d.toFormat("dd'd,' hh'h,' mm'm,' ss's'");
};

// Health scores
export const healthValueFormatter = (value: number) => {
  if (value < 0) return 'N/A';
  if (value < 0.25) return 'Unhealthy';
  if (value < 0.75) return 'Fair';
  return 'Healthy';
};

export const healthVariantFormatter = (value: number) => {
  if (value < 0) return 'neutral';
  if (value < 0.25) return 'negative';
  if (value < 0.75) return 'attention';
  return 'positive';
};
